import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Printer, ArrowLeft, Loader2, ArrowRight, TrendingUp, MessageCircle, DollarSign, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, Type, Minus, Undo2, Redo2, Heading1, Heading2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { useTheme } from '../../contexts/ThemeContext';
import { concorrentesAPI } from '../../services/api';
import { toast } from 'react-toastify';

// Convert AI roteiro JSON to HTML document
function str(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(str).join(', ');
  if (typeof val === 'object') {
    return Object.entries(val).map(([k, v]) => `${k}: ${str(v)}`).join(' • ');
  }
  return String(val);
}

function roteiroToHtml(roteiro, ideia, dark) {
  if (!roteiro || !ideia) return '';
  let html = '';
  const tipo = ideia.tipo;

  // Colors based on theme
  const c = dark ? {
    muted: '#9ca3af', accent: '#a78bfa', bg2: '#27272a', bg3: '#3f3f46', border: '#52525b',
    scoreGreen: '#052e16', scoreYellow: '#422006', scoreRed: '#450a0a',
    scoreGreenTxt: '#4ade80', scoreYellowTxt: '#facc15', scoreRedTxt: '#f87171',
    blockBg: '#27272a', blockBorder: '#7c3aed', slideBg: '#27272a',
    capaBg: '#2e1065', ctaBg: '#451a03', legendaBg: '#172554', legendaBorder: '#1e40af',
    hashColor: '#818cf8',
  } : {
    muted: '#888', accent: '#7c3aed', bg2: '#f8f8f8', bg3: '#f3f4f6', border: '#e5e7eb',
    scoreGreen: '#f0fdf4', scoreYellow: '#fefce8', scoreRed: '#fef2f2',
    scoreGreenTxt: '#22c55e', scoreYellowTxt: '#eab308', scoreRedTxt: '#ef4444',
    blockBg: '#f8f8f8', blockBorder: '#7c3aed', slideBg: '#f8f8f8',
    capaBg: '#f5f3ff', ctaBg: '#fef3c7', legendaBg: '#f0f9ff', legendaBorder: '#bae6fd',
    hashColor: '#3b82f6',
  };

  html += `<h1>${ideia.titulo}</h1>`;
  html += `<p style="color:${c.muted};font-size:13px;">${tipo?.toUpperCase()} • Tom: ${roteiro.tom || 'educativo'}</p>`;
  html += `<hr style="border:none;border-top:1px solid ${c.border};margin:20px 0;"/>`;

  if (roteiro.score) {
    html += `<h2>📊 Score de Potencial</h2>`;
    html += `<table style="width:100%;border-collapse:separate;border-spacing:8px;margin:12px 0;"><tr>`;
    html += `<td style="padding:16px;text-align:center;background:${c.scoreGreen};border-radius:12px;"><strong style="font-size:28px;color:${c.scoreGreenTxt};">${roteiro.score.viralizacao}/10</strong><br/><span style="font-size:12px;color:${c.muted};">🔥 Viralização</span></td>`;
    html += `<td style="padding:16px;text-align:center;background:${c.scoreYellow};border-radius:12px;"><strong style="font-size:28px;color:${c.scoreYellowTxt};">${roteiro.score.engajamento}/10</strong><br/><span style="font-size:12px;color:${c.muted};">💬 Engajamento</span></td>`;
    html += `<td style="padding:16px;text-align:center;background:${c.scoreRed};border-radius:12px;"><strong style="font-size:28px;color:${c.scoreRedTxt};">${roteiro.score.conversao}/10</strong><br/><span style="font-size:12px;color:${c.muted};">💰 Conversão</span></td>`;
    html += `</tr></table>`;
    if (roteiro.score_motivo) html += `<p style="color:${c.muted};font-style:italic;font-size:13px;">${str(roteiro.score_motivo)}</p>`;
  }

  if (roteiro.publico_alvo) {
    html += `<h2>👥 Público-alvo</h2><p>${str(roteiro.publico_alvo)}</p>`;
  }

  if (roteiro.referencia_visual) {
    html += `<h2>🎨 Referência Visual</h2><p>${str(roteiro.referencia_visual)}</p>`;
  }

  if (roteiro.melhores_horarios?.length) {
    html += `<h2>⏰ Melhores Horários</h2><ul>`;
    roteiro.melhores_horarios.forEach(h => {
      html += `<li>${str(h)}</li>`;
    });
    html += `</ul>`;
  }

  html += `<hr style="border:none;border-top:1px solid ${c.border};margin:20px 0;"/>`;

  // REELS
  if (tipo === 'reels') {
    if (roteiro.hook) html += `<h2>🎣 Hook (primeiros 3 segundos)</h2><p style="font-size:20px;font-weight:bold;color:${c.accent};">"${roteiro.hook}"</p>`;
    
    if (roteiro.ganchos_alternativos?.length) {
      html += `<p style="color:${c.muted};font-size:13px;margin-top:8px;"><strong>Ganchos alternativos:</strong></p><ul>`;
      roteiro.ganchos_alternativos.forEach(g => { html += `<li style="color:${c.muted};font-size:13px;">${g}</li>`; });
      html += `</ul>`;
    }

    if (roteiro.estrutura_narrativa) html += `<p style="color:${c.muted};font-style:italic;margin:12px 0;">📐 <strong>Estrutura:</strong> ${str(roteiro.estrutura_narrativa)}</p>`;
    if (roteiro.musica_sugerida) html += `<p>🎵 <strong>Música:</strong> ${str(roteiro.musica_sugerida)}</p>`;
    if (roteiro.thumbnail) html += `<p>🖼 <strong>Thumbnail:</strong> ${str(roteiro.thumbnail)}</p>`;

    if (roteiro.cenas?.length) {
      html += `<h2>🎬 Roteiro — Cenas (${roteiro.cenas.length} cenas)</h2>`;
      roteiro.cenas.forEach((cena, i) => {
        html += `<div style="background:${c.blockBg};border-left:4px solid ${c.blockBorder};padding:16px 20px;margin:16px 0;border-radius:0 12px 12px 0;">`;
        html += `<p style="margin:0 0 12px;font-size:15px;"><strong>Cena ${cena.numero || i + 1} — ${cena.tempo || ''}</strong>`;
        if (cena.transicao) html += ` <span style="color:${c.muted};font-size:12px;background:${c.bg3};padding:2px 8px;border-radius:4px;">↗ ${cena.transicao}</span>`;
        html += `</p>`;
        if (cena.fala) html += `<p style="margin:8px 0;line-height:1.8;">🗣 <strong>Fala:</strong> ${str(cena.fala)}</p>`;
        if (cena.visual) html += `<p style="margin:8px 0;color:${c.muted};line-height:1.7;">👁 <strong>Visual:</strong> ${str(cena.visual)}</p>`;
        if (cena.texto_overlay) html += `<p style="margin:8px 0;color:${c.accent};line-height:1.7;">📝 <strong>Texto na tela:</strong> ${str(cena.texto_overlay)}</p>`;
        if (cena.nota_edicao) html += `<p style="margin:8px 0;color:${c.muted};font-size:13px;font-style:italic;">✂️ <strong>Edição:</strong> ${str(cena.nota_edicao)}</p>`;
        html += `</div>`;
      });
    }

    if (roteiro.cta) html += `<h2>📢 CTA Final</h2><p style="font-weight:bold;font-size:16px;">${roteiro.cta}</p>`;
    if (roteiro.duracao_estimada) html += `<p style="color:${c.muted};">⏱ Duração estimada: ~${roteiro.duracao_estimada}s</p>`;

    if (roteiro.dicas_gravacao?.length) {
      html += `<h2>💡 Dicas de Gravação</h2><ol>`;
      roteiro.dicas_gravacao.forEach(d => { html += `<li style="margin:6px 0;line-height:1.6;">${d}</li>`; });
      html += `</ol>`;
    }
  }

  // CARROSSEL
  if (tipo === 'carrossel') {
    if (roteiro.fluxo_narrativo) html += `<h2>📖 Fluxo Narrativo</h2><p>${str(roteiro.fluxo_narrativo)}</p>`;
    if (roteiro.paleta_cores?.length) {
      html += `<p style="margin:8px 0;">🎨 <strong>Paleta:</strong> `;
      roteiro.paleta_cores.forEach(cor => { html += `<span style="display:inline-block;width:24px;height:24px;background:${cor};border-radius:6px;margin:0 4px;vertical-align:middle;border:1px solid ${c.border};"></span><code style="font-size:11px;color:${c.muted};">${cor}</code> `; });
      html += `</p>`;
    }
    if (roteiro.tipografia) html += `<p>🔤 <strong>Tipografia:</strong> ${str(roteiro.tipografia)}</p>`;

    if (roteiro.slides?.length) {
      html += `<h2>📑 Slides (${roteiro.slides.length} slides)</h2>`;
      roteiro.slides.forEach((s, i) => {
        const isCapa = i === 0;
        const isCta = i === roteiro.slides.length - 1;
        const bg = isCapa ? c.capaBg : isCta ? c.ctaBg : c.slideBg;
        const brd = isCapa ? c.accent : isCta ? '#f59e0b' : '#3b82f6';
        html += `<div style="background:${bg};border-left:4px solid ${brd};padding:16px 20px;margin:16px 0;border-radius:0 12px 12px 0;">`;
        html += `<p style="margin:0 0 4px;font-size:12px;color:${c.muted};">${isCapa ? '📌 CAPA' : isCta ? '🎯 CTA FINAL' : `Slide ${s.numero || i + 1}`}${s.tipo_slide ? ` • ${s.tipo_slide}` : ''}</p>`;
        html += `<p style="margin:0 0 10px;font-size:18px;font-weight:bold;">${s.titulo}</p>`;
        if (s.corpo) html += `<p style="margin:8px 0;line-height:1.7;">${str(s.corpo)}</p>`;
        if (s.dica_visual) html += `<p style="margin:6px 0;color:${c.muted};font-style:italic;font-size:13px;">🎨 ${str(s.dica_visual)}</p>`;
        if (s.nota_design) html += `<p style="margin:6px 0;color:${c.muted};font-style:italic;font-size:13px;">✏️ ${str(s.nota_design)}</p>`;
        html += `</div>`;
      });
    }
  }

  // FOTO
  if (tipo === 'foto') {
    if (roteiro.primeira_linha) html += `<h2>👆 Primeira Linha (Gancho)</h2><p style="font-size:18px;font-weight:bold;color:${c.accent};">"${roteiro.primeira_linha}"</p>`;
    if (roteiro.dica_visual) html += `<h2>📸 Direção Visual</h2><p style="line-height:1.8;">${str(roteiro.dica_visual)}</p>`;
    if (roteiro.alternativas_foto?.length) {
      html += `<h2>📷 Alternativas de Foto</h2><ol>`;
      roteiro.alternativas_foto.forEach(a => { html += `<li style="margin:8px 0;line-height:1.6;">${a}</li>`; });
      html += `</ol>`;
    }
    if (roteiro.cta) html += `<h2>📢 CTA</h2><p style="font-weight:bold;">${roteiro.cta}</p>`;
    if (roteiro.stories_complementares?.length) {
      html += `<h2>📱 Stories Complementares</h2>`;
      roteiro.stories_complementares.forEach((s, i) => {
        html += `<div style="background:${c.blockBg};border-left:4px solid ${c.accent};padding:12px 16px;margin:10px 0;border-radius:0 12px 12px 0;">`;
        html += `<p style="margin:0;"><strong>Story ${i + 1}:</strong> ${str(s)}</p>`;
        html += `</div>`;
      });
    }
  }

  // KPIs
  if (roteiro.kpis_esperados) {
    html += `<hr style="border:none;border-top:1px solid ${c.border};margin:20px 0;"/>`;
    html += `<h2>📈 KPIs Esperados</h2>`;
    html += `<p>${str(roteiro.kpis_esperados)}</p>`;
  }

  html += `<hr style="border:none;border-top:1px solid ${c.border};margin:20px 0;"/>`;

  if (roteiro.legenda_final) {
    html += `<h2>📝 Legenda Final</h2>`;
    html += `<div style="background:${c.legendaBg};border:1px solid ${c.legendaBorder};border-radius:12px;padding:20px;white-space:pre-line;line-height:1.8;">${roteiro.legenda_final}</div>`;
  }

  if (roteiro.hashtags_otimizadas?.length) {
    html += `<h2># Hashtags</h2>`;
    html += `<p style="color:${c.hashColor};line-height:2;">${roteiro.hashtags_otimizadas.join('  ')}</p>`;
  }

  return html;
}

const ToolbarBtn = ({ onClick, active, children, title, darkMode }) => (
  <button onClick={onClick} title={title}
    className={`p-1.5 rounded transition-colors ${active
      ? 'bg-blue-600 text-white'
      : darkMode ? 'text-gray-400 hover:bg-zinc-700 hover:text-white' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`}>
    {children}
  </button>
);

const ToolbarSep = ({ darkMode }) => <div className={`w-px h-6 mx-1 ${darkMode ? 'bg-zinc-700' : 'bg-gray-300'}`} />;

export default function RoteiroEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const editorRef = useRef(null);
  const [roteiro, setRoteiro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('rascunho');
  const [titulo, setTitulo] = useState('');

  useEffect(() => {
    loadRoteiro();
  }, [id]); // eslint-disable-line

  const loadRoteiro = async () => {
    try {
      const res = await concorrentesAPI.getRoteiro(id);
      const r = res.data;
      setRoteiro(r);
      setStatus(r.status);
      setTitulo(r.titulo);
      setTimeout(() => {
        if (editorRef.current) {
          // Always regenerate from JSON to match current theme; use _html only if explicitly edited
          const hasEdited = r.conteudo?._html_edited;
          editorRef.current.innerHTML = hasEdited ? r.conteudo._html : roteiroToHtml(r.conteudo, r.ideia_original, darkMode);
        }
      }, 50);
    } catch { toast.error('Roteiro não encontrado'); navigate('/insights/ideias'); }
    finally { setLoading(false); }
  };

  const exec = useCallback((cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const html = editorRef.current?.innerHTML || '';
      await concorrentesAPI.updateRoteiro(id, {
        conteudo: { ...roteiro.conteudo, _html: html, _html_edited: true },
        status,
        titulo,
      });
      toast.success('Salvo!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const print = () => {
    const content = editorRef.current?.innerHTML || '';
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${titulo}</title>
      <style>
        body { font-family: 'Segoe UI', system-ui, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; line-height: 1.6; }
        h1 { font-size: 28px; margin-bottom: 4px; }
        h2 { font-size: 18px; margin-top: 24px; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
        table { width: 100%; }
        td { padding: 12px; text-align: center; }
        hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
        div[style*="border-left"] { page-break-inside: avoid; }
        @media print { body { margin: 20px; } }
      </style>
    </head><body>${content}</body></html>`);
    win.document.close();
    win.print();
  };

  const usarComoPost = () => {
    if (!roteiro) return;
    const r = roteiro.conteudo;
    const legenda = r.legenda_final || roteiro.ideia_original?.legenda || '';
    const hashtags = r.hashtags_otimizadas || roteiro.ideia_original?.hashtags || [];
    const tipo = roteiro.ideia_original?.tipo;
    navigate('/insights/publicacoes', { state: { prefill: { caption: legenda + '\n\n' + hashtags.join(' '), tipo: tipo === 'reels' ? 'video' : tipo === 'carrossel' ? 'carousel' : 'image' } } });
  };

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    </Layout>
  );

  const bg = darkMode ? 'bg-zinc-900' : 'bg-gray-100';
  const toolbarBg = darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200';
  const editorBg = darkMode ? 'bg-zinc-800 text-gray-200' : 'bg-white text-gray-900';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <Layout>
      <div className={`min-h-screen ${bg}`}>
        {/* Top bar */}
        <div className={`sticky top-0 z-20 border-b ${toolbarBg} shadow-sm`}>
          <div className="max-w-5xl mx-auto px-4 py-2 flex items-center gap-3">
            <button onClick={() => navigate('/insights/ideias')}
              className={`p-2 rounded-lg ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
              <ArrowLeft className="w-5 h-5" />
            </button>
            <input value={titulo} onChange={e => setTitulo(e.target.value)}
              className={`flex-1 text-lg font-semibold bg-transparent border-0 focus:ring-0 ${darkMode ? 'text-white' : 'text-gray-900'}`}
              placeholder="Título do roteiro" />
            <select value={status} onChange={e => setStatus(e.target.value)}
              className={`text-xs rounded-lg px-3 py-1.5 border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-gray-300' : 'bg-gray-50 border-gray-200'}`}>
              <option value="rascunho">📝 Rascunho</option>
              <option value="aprovado">✅ Aprovado</option>
              <option value="usado">📤 Usado</option>
            </select>
            <button onClick={save} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
            </button>
            <button onClick={print}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-zinc-600 text-gray-300 hover:bg-zinc-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
              <Printer className="w-4 h-4" /> Imprimir
            </button>
            <button onClick={usarComoPost}
              className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">
              <ArrowRight className="w-4 h-4" /> Criar post
            </button>
          </div>

          {/* Formatting toolbar */}
          <div className={`max-w-5xl mx-auto px-4 py-1.5 flex items-center gap-0.5 flex-wrap border-t ${darkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
            <ToolbarBtn onClick={() => exec('undo')} title="Desfazer" darkMode={darkMode}><Undo2 className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('redo')} title="Refazer" darkMode={darkMode}><Redo2 className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep darkMode={darkMode} />
            <ToolbarBtn onClick={() => exec('formatBlock', 'h1')} title="Título 1" darkMode={darkMode}><Heading1 className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('formatBlock', 'h2')} title="Título 2" darkMode={darkMode}><Heading2 className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('formatBlock', 'p')} title="Parágrafo" darkMode={darkMode}><Type className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep darkMode={darkMode} />
            <ToolbarBtn onClick={() => exec('bold')} title="Negrito (Ctrl+B)" darkMode={darkMode}><Bold className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('italic')} title="Itálico (Ctrl+I)" darkMode={darkMode}><Italic className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('underline')} title="Sublinhado (Ctrl+U)" darkMode={darkMode}><Underline className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep darkMode={darkMode} />
            <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Lista" darkMode={darkMode}><List className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('insertOrderedList')} title="Lista numerada" darkMode={darkMode}><ListOrdered className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep darkMode={darkMode} />
            <ToolbarBtn onClick={() => exec('justifyLeft')} title="Alinhar esquerda" darkMode={darkMode}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => exec('justifyCenter')} title="Centralizar" darkMode={darkMode}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep darkMode={darkMode} />
            <ToolbarBtn onClick={() => exec('insertHorizontalRule')} title="Linha divisória" darkMode={darkMode}><Minus className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep darkMode={darkMode} />
            <select onChange={e => { if (e.target.value) exec('fontSize', e.target.value); e.target.value = ''; }}
              className={`text-xs rounded px-2 py-1 border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-gray-300' : 'bg-white border-gray-200'}`}>
              <option value="">Tamanho</option>
              <option value="1">Pequeno</option>
              <option value="3">Normal</option>
              <option value="5">Grande</option>
              <option value="7">Enorme</option>
            </select>
            <select onChange={e => { if (e.target.value) exec('foreColor', e.target.value); e.target.value = ''; }}
              className={`text-xs rounded px-2 py-1 border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-gray-300' : 'bg-white border-gray-200'}`}>
              <option value="">Cor</option>
              <option value="#000000">⬛ Preto</option>
              <option value="#ef4444">🟥 Vermelho</option>
              <option value="#3b82f6">🟦 Azul</option>
              <option value="#22c55e">🟩 Verde</option>
              <option value="#7c3aed">🟪 Roxo</option>
              <option value="#f59e0b">🟨 Amarelo</option>
              <option value="#888888">⬜ Cinza</option>
            </select>
          </div>
        </div>

        {/* Editor area - Google Docs style */}
        <div className="max-w-5xl mx-auto py-8 px-4">
          <div className={`rounded-xl shadow-lg border ${darkMode ? 'border-zinc-700' : 'border-gray-200'} overflow-hidden`}>
            <style>{`
              .roteiro-editor h1 { font-size: 26px; font-weight: 800; margin: 0 0 8px; color: ${darkMode ? '#f4f4f5' : '#111827'}; }
              .roteiro-editor h2 { font-size: 17px; font-weight: 700; margin: 28px 0 10px; padding-bottom: 6px; border-bottom: 1px solid ${darkMode ? '#3f3f46' : '#e5e7eb'}; color: ${darkMode ? '#d4d4d8' : '#374151'}; }
              .roteiro-editor p { margin: 8px 0; line-height: 1.7; }
              .roteiro-editor ul, .roteiro-editor ol { margin: 8px 0; padding-left: 24px; }
              .roteiro-editor li { margin: 4px 0; line-height: 1.6; }
              .roteiro-editor hr { border: none; border-top: 1px solid ${darkMode ? '#3f3f46' : '#e5e7eb'}; margin: 24px 0; }
              .roteiro-editor table { width: 100%; border-collapse: separate; border-spacing: 8px; }
              .roteiro-editor td { padding: 16px; text-align: center; border-radius: 12px; }
              .roteiro-editor strong { color: ${darkMode ? '#e4e4e7' : '#111827'}; }
            `}</style>
            <div ref={editorRef} contentEditable suppressContentEditableWarning
              className={`roteiro-editor min-h-[800px] px-16 py-12 text-base leading-relaxed focus:outline-none ${editorBg}`}
              style={{ fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}
              onKeyDown={e => {
                if (e.ctrlKey || e.metaKey) {
                  if (e.key === 's') { e.preventDefault(); save(); }
                }
              }}
            />
          </div>
          <p className={`text-center text-xs mt-4 ${muted}`}>
            Ctrl+S para salvar • Ctrl+B negrito • Ctrl+I itálico • Ctrl+U sublinhado
          </p>
        </div>
      </div>
    </Layout>
  );
}
